import { useState, FC, useMemo, useEffect, useCallback } from "react";
import {
  AuthenticationProviderVarieties,
  AuthenticationProviderType,
} from "@vviinn/user-managment-client-ts";

import { RenderStep } from "./RenderStep";
import { ErrorWrapper } from "../../wrappers";
import { useAuthWrapper } from "../AuthWrapperContext";
import {
  SubmitFormElement,
  BUTTON_MAIN_CLASSNAME,
  FormStep,
  NameIdentifiers,
  ReCaptcha,
  TermsAndConditionsNotice,
} from "../../general";
import { StringStateAction } from "../../../helpers/functions/types";
import { LoaderIcon } from "../../../ui/icons";
import { DEFAULT_ERROR_WITH_SUGGESTION_MESSAGE } from "../../../ui/constants";

type AuthFormProps = {
  submitTitle: string;
  steps: FormStep[];
  loginSubmitCallback?: (
    email?: string,
    password?: string,
    providerId?: string,
    providerType?: AuthenticationProviderType,
  ) => void | Promise<void>;
  registerSubmitCallback?: (
    formSteps: FormStep[],
    silentSubmit?: boolean,
  ) => Promise<void>;
  getConfigs?: (
    email: string,
    recaptchaToken: string,
  ) => Promise<AuthenticationProviderVarieties>;
  errorMessage: string;
  setErrorMessage: StringStateAction;
  isLoading?: boolean;
  needRecaptcha?: boolean;
  needTermsAndConditions?: boolean;
};

export const AuthForm: FC<React.PropsWithChildren<AuthFormProps>> = ({
  submitTitle,
  loginSubmitCallback,
  registerSubmitCallback,
  steps: mainSteps,
  getConfigs,
  errorMessage,
  setErrorMessage,
  isLoading,
  needRecaptcha = false,
  needTermsAndConditions = false,
}) => {
  const [steps, setSteps] = useState(mainSteps);
  const [loading, setLoading] = useState(isLoading);
  const [step, setStep] = useState(mainSteps[0].name);

  const [authConfig, setAuthConfig] =
    useState<AuthenticationProviderVarieties>();

  const { setIsSigningIn } = useAuthWrapper();

  const isPassword =
    !authConfig || authConfig.authenticationMode === "email_password";

  // The per-email lookup is a public, unauthenticated endpoint, so it can't
  // reveal whether a plain email/password account already exists (that
  // would let anyone probe which emails are registered) — it always
  // resolves to "email_password" by default. SSO is the only signal it's
  // safe to act on: it describes the organization's auth requirement, not
  // a specific account. So registering with an email whose org requires
  // SSO switches this step to "continue with that provider" instead of
  // creating a password.
  const isExistingSsoAccount = !!registerSubmitCallback && !isPassword;

  useEffect(() => {
    setIsSigningIn(isExistingSsoAccount);
  }, [isExistingSsoAccount, setIsSigningIn]);

  const setValue =
    (name: NameIdentifiers) => (value: string | boolean | number) => {
      const index = steps.findIndex((s) => s.name === name);
      steps[index].value = value;
      setSteps([...steps]);
      setErrorMessage("");
    };

  const findStepByName = useCallback(
    (name: NameIdentifiers) => steps.find((s) => s.name === name),
    [steps],
  );

  useEffect(() => {
    setLoading(isLoading);
  }, [isLoading]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (currentStep?.end || currentStep?.silentSubmit) {
      const email = findStepByName("email")?.value;
      const password = findStepByName("password")?.value;

      if (registerSubmitCallback) {
        registerSubmitCallback(steps, currentStep?.silentSubmit);
      } else if (loginSubmitCallback) {
        loginSubmitCallback(email?.toString(), password?.toString());
      }
    } else if (currentStep?.next) {
      if (getConfigs && currentStep?.next === "password") {
        setLoading(true);
        try {
          const emailStep = findStepByName("email");
          const config = emailStep?.value
            ? await getConfigs(emailStep.value.toString(), "")
            : undefined;
          setAuthConfig(config);
          setErrorMessage("");
          // A resolved SSO config is unambiguous (one provider, tied to
          // either the existing account's company or an email-domain match)
          // so there's nothing left to pick — sign in immediately instead of
          // rendering a list to choose from.
          if (config && config.authenticationMode !== "email_password") {
            await loginSubmitCallback?.(
              undefined,
              undefined,
              config.id,
              config.authenticationProvider,
            );
            // When the parent controls loading via `isLoading`, stay in the
            // loading state: control belongs to the in-flight sign-in, which
            // drives its own `isLoading` prop back down. Clearing it here
            // would briefly re-enable this step's Next button before that
            // takes over. When `isLoading` isn't provided, this component
            // owns loading itself, so it must clear it once the callback
            // settles or the spinner spins forever on error/cancel.
            if (isLoading === undefined) {
              setLoading(false);
            }
            return;
          } else {
            setStep(currentStep.next);
          }
        } catch (e) {
          setErrorMessage(DEFAULT_ERROR_WITH_SUGGESTION_MESSAGE);
        }
        setLoading(false);
      } else setStep(currentStep.next);
    }
  };

  useEffect(() => {
    setSteps(mainSteps);
    setStep(mainSteps[0].name);
  }, [mainSteps]);

  const currentStep = useMemo(
    () => findStepByName(step),
    [step, findStepByName],
  );

  const handleGoBack = useCallback(() => {
    if (!currentStep) return;

    setStep(currentStep.previous as NameIdentifiers);
    if (setErrorMessage) {
      setErrorMessage("");
    }
  }, [currentStep, setErrorMessage]);

  return (
    <ErrorWrapper errorMessage={errorMessage}>
      <form onSubmit={handleSubmit}>
        {loading ? (
          <div className="flex justify-center">
            <LoaderIcon className="animate-spin" />
          </div>
        ) : (
          <>
            {currentStep && (
              <>
                <RenderStep
                  findStepByName={findStepByName}
                  step={currentStep}
                  isPassword={isPassword}
                  setValue={setValue}
                />

                {currentStep.next && (
                  <SubmitFormElement submitTitle="Next" disabled={!!loading} />
                )}

                {currentStep.end && (
                  <>
                    {needRecaptcha && (
                      <ReCaptcha
                        errorMessage={errorMessage}
                        actionName="signup"
                        showNextButton={false}
                        setErrorMessage={setErrorMessage}
                      />
                    )}

                    <SubmitFormElement
                      submitTitle={submitTitle}
                      disabled={!!loading}
                    >
                      {needTermsAndConditions && <TermsAndConditionsNotice />}
                    </SubmitFormElement>
                  </>
                )}

                {currentStep.previous && (
                  <button
                    className={`${BUTTON_MAIN_CLASSNAME} mt-2 bg-stone-500 text-stone-700 hover:bg-stone-600 hover:text-white dark:bg-stone-500 dark:text-white dark:hover:bg-stone-600`}
                    onClick={handleGoBack}
                    type="button"
                  >
                    Back
                  </button>
                )}
              </>
            )}
          </>
        )}
      </form>
    </ErrorWrapper>
  );
};
